import React, { useEffect, useState } from "react";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import ModalLayout from "../layouts/ModalLayout";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { toast } from "sonner";
import TimeTableApis from "../../api/TimeTableApis";
import { useDispatch } from "react-redux";
import { fetchStaff } from "../../features/staff/staffSlice";

interface Props {
  day: string;
  period: string;
  classId: number;
  branchCode: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

function AddTimeTable({
  day,
  period,
  classId,
  branchCode,
  onClose,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const branchId = useSelector(
    (state: RootState) => state.access.schoolBranch?.schoolBranchId,
  );
  const dispatch = useDispatch<AppDispatch>();
  const { staffList } = useSelector((state: RootState) => state.staff);
  const { subjectEnabled } = useSelector((state: RootState) => state.access);

  const [formData, setFormData] = useState({
    subjectId: "",
    staffId: "",
    subjectName: "",
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "subjectId") {
      const selected = subjectEnabled.find((s) => s.subjectId === Number(value));

      setFormData((prev) => ({
        ...prev,
        subjectId: value,
        subjectName: selected?.subjectName || "",
        staffId: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const dayMap: Record<string, string> = {
    Mon: "MONDAY",
    Tue: "TUESDAY",
    Wed: "WEDNESDAY",
    Thu: "THURSDAY",
    Fri: "FRIDAY",
    Sat: "SATURDAY",
    Sun: "SUNDAY",
  };

  useEffect(() => {
    if (!branchCode || !formData.subjectName) return;

    dispatch(
      fetchStaff({
        branchCode,
        page: 0,
        size: 100,
        subjectName: formData.subjectName,
      }),
    );
  }, [formData.subjectName, branchCode, dispatch]);

  useEffect(() => {
    if (!formData.subjectName) return;

    if (staffList.length === 0) {
      toast.warning("No staff available for selected subject");
    }
  }, [staffList]);

  const formattedDay = dayMap[day];

  const periodNumber = Number(period.replace("Period ", ""));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subjectId) {
      toast.error("Please select a subject");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        classId,
        subjectId: Number(formData.subjectId),
        staffId: Number(formData.staffId),
        day: formattedDay,
        periodNumber,
        branchCode,
      };

      const response = await TimeTableApis.createTimeTable(payload);

      if (response?.status === 1) {
        toast.success("Time table added successfully");
        onSuccess?.();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalLayout onClose={onClose}>
      <form className="flex flex-col gap-8 max-w-xl" onSubmit={handleSubmit}>
        <CustomSelect
          name="subjectId"
          label="Select Subject"
          value={formData.subjectId}
          options={subjectEnabled?.map((item) => ({
            label: item.subjectName,
            value: item.subjectId,
          }))}
          placeholder="Select subject that you want to add"
          onChange={handleInputChange}
        />

        <CustomSelect
          name="staffId"
          label="Select Teacher"
          value={formData.staffId}
          options={staffList?.map((item) => ({
            label: item.fullName,
            value: item.staffId,
          }))}
          placeholder={
            staffList?.length === 0
              ? "No staff available"
              : "Select staff that you want to assign"
          }
          onChange={handleInputChange}
          disabled={!formData.subjectId || staffList.length === 0}
        />

        <div className="flex justify-end gap-3">
          <CustomButton
            color="bg-neutral-200"
            text="Cancel"
            onClick={onClose}
            disabled={loading}
          />
          <CustomButton
            color="bg-primary-500"
            text="Save"
            type="submit"
            disabled={loading || !formData.subjectId || !formData.staffId}
          />
        </div>
      </form>
    </ModalLayout>
  );
}

export default AddTimeTable;
